import dayjs from "dayjs";
import "dayjs/locale/zh-cn";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration from "dayjs/plugin/duration";
import localeData from "dayjs/plugin/localeData";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";
import weekday from "dayjs/plugin/weekday";
import { hasExplicitTimezone } from "@/utils/dateParsing.mjs";

export const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm";
export const DATE_FORMAT = "YYYY-MM-DD";
export const MONTH_FORMAT = "YYYY-MM";
export const YEAR_FORMAT = "YYYY";
export const DATE_SLASH_FORMAT = "YYYY/MM/DD";
export const LOCAL_DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ss";
export const DATE_RANGE_START_FORMAT = "YYYY-MM-DD 00:00:00";
export const DATE_RANGE_END_FORMAT = "YYYY-MM-DD 23:59:59";

function toDayjs(value) {
  if (!value) {
    return null;
  }
  if (dayjs.isDayjs(value)) {
    return value;
  }
  if (hasExplicitTimezone(value)) {
    return dayjs(value).tz();
  }
  return dayjs.tz(value);
}

export function formatDateValue(value, pattern = DATE_FORMAT, defaultText = "-") {
  const date = toDayjs(value);
  if (!date || !date.isValid()) {
    return defaultText;
  }
  return date.format(pattern);
}

export function formatDateTime(value, defaultText = "-") {
  return formatDateValue(value, DATE_TIME_FORMAT, defaultText);
}

export function formatDate(value, defaultText = "-") {
  return formatDateValue(value, DATE_FORMAT, defaultText);
}

export function formatMonth(value, defaultText = "-") {
  return formatDateValue(value, MONTH_FORMAT, defaultText);
}

export function formatYear(value, defaultText = "-") {
  return formatDateValue(value, YEAR_FORMAT, defaultText);
}

export function formatDateSlash(value, defaultText = "-") {
  return formatDateValue(value, DATE_SLASH_FORMAT, defaultText);
}

export function formatLocalDateTime(value, defaultText = "-") {
  return formatDateValue(value, LOCAL_DATE_TIME_FORMAT, defaultText);
}

export function formatDateRangeStart(value, defaultText = value) {
  return formatDateValue(value, DATE_RANGE_START_FORMAT, defaultText);
}

export function formatDateRangeEnd(value, defaultText = value) {
  return formatDateValue(value, DATE_RANGE_END_FORMAT, defaultText);
}

export function formatCurrentMonth(defaultText = "-") {
  return formatMonth(dayjs(), defaultText);
}

export function getMonthBetween(startDateStr, endDateStr) {
  const startDate = dayjs(startDateStr).startOf("month");
  const endDate = dayjs(endDateStr).endOf("month");

  const result = [];
  result.push(formatMonth(startDate));
  let tempMonth = startDate;
  while (tempMonth.isBefore(endDate, "month")) {
    tempMonth = tempMonth.add(1, "month");
    result.push(formatMonth(tempMonth));
  }
  return result.reverse();
}

export function setupDayjs() {
  dayjs.locale("zh-cn");
  dayjs.extend(customParseFormat);
  dayjs.extend(advancedFormat);
  dayjs.extend(weekday);
  dayjs.extend(localeData);
  dayjs.extend(weekOfYear);
  dayjs.extend(weekYear);
  dayjs.extend(duration);
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.tz.setDefault("Asia/Shanghai");
}
